import qualityComplaintsQueries from '@/api/modules/qualityComplaintsQueries'
import { COMPLAINTS } from '@/constants'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import { handleApiError } from '@/utils/apiHelpers'

const prepareComplaintPayload = (payload) => {
    const isExternalComplaint = payload?.complaintTypeId === COMPLAINTS.COMPLAINT_TYPE.EXTERNAL

    if (!isExternalComplaint || !payload) {
        return payload
    }

    const sanitizedPayload = { ...payload }
    delete sanitizedPayload.customer

    return sanitizedPayload
}

class QualityComplaintsService {
    async fetchComplaints(queryString = '') {
        try {
            const response = await qualityComplaintsQueries.fetchComplaints(queryString)
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.fetchList'))
            throw error
        }
    }

    async fetchComplaint(id) {
        try {
            const response = await qualityComplaintsQueries.fetchComplaintById(id)
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.fetchComplaint'))
            throw error
        }
    }

    async createComplaint(payload) {
        try {
            const response = await qualityComplaintsQueries.createComplaint(prepareComplaintPayload(payload))
            notificationService.success(t('quality.complaints.notifications.createSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.create'))
            throw error
        }
    }

    async updateComplaint(payload) {
        try {
            const id = payload?.id
            const response = await qualityComplaintsQueries.updateComplaint(id, prepareComplaintPayload(payload))
            notificationService.success(t('quality.complaints.notifications.updateSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.update'))
            throw error
        }
    }

    async fetchComplaintErrors() {
        try {
            const response = await qualityComplaintsQueries.fetchComplaintErrors()
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.fetchErrors'))
            throw error
        }
    }

    async createComplaintError(payload) {
        try {
            const response = await qualityComplaintsQueries.createComplaintError(payload)
            notificationService.success(t('quality.complaints.causes.addErrorSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.causes.addErrorError'))
            throw error
        }
    }

    async createComplaintComment(payload) {
        try {
            const response = await qualityComplaintsQueries.createComplaintComment(payload)
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.create'))
            throw error
        }
    }

    async createAttachment(formData, config = {}) {
        try {
            const response = await qualityComplaintsQueries.uploadComplaintAttachment(formData, config)
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.createAttachment'))
            throw error
        }
    }

    async deleteAttachment(id) {
        try {
            const response = await qualityComplaintsQueries.deleteComplaintAttachment(id)
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.deleteAttachment'))
            throw error
        }
    }

    async generateComplaintsReport(queryString = '', userEmail) {
        try {
            const response = await qualityComplaintsQueries.generateComplaintsReport(queryString)
            notificationService.success(
                t('quality.complaints.notifications.reportSuccess', {
                    email: userEmail
                })
            )
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.report'))
            throw error
        }
    }

    async downloadComplaintCard(complaintId) {
        try {
            const response = await qualityComplaintsQueries.downloadComplaintCard({
                complaintId,
                html: 'print_complaint_card'
            })

            if (response?.data) {
                const blob = new Blob([response.data], { type: 'application/pdf' })
                const url = window.URL.createObjectURL(blob)
                window.open(url, '_blank', 'noopener,noreferrer')
            }

            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.printComplaintCard'))
            throw error
        }
    }

    async fetchComplaintStatusHistory(id) {
        try {
            const response = await qualityComplaintsQueries.fetchComplaintStatusHistory(id)
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.fetchStatusHistory'))
            throw error
        }
    }

    async deleteComplaint(id) {
        try {
            const response = await qualityComplaintsQueries.deleteComplaint(id)
            notificationService.success(t('quality.complaints.notifications.deleteSuccess'))
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.deleteComplaint'))
            throw error
        }
    }

    async fetchComplaintTypes() {
        try {
            const response = await qualityComplaintsQueries.fetchComplaintTypes()
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.fetchTypes'))
            throw error
        }
    }

    async fetchComplaintStatuses() {
        try {
            const response = await qualityComplaintsQueries.fetchComplaintStatuses()
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.fetchStatuses'))
            throw error
        }
    }

    async fetchProductionDepartments(filter = null) {
        try {
            const response = await qualityComplaintsQueries.fetchProductionDepartments(filter)
            return response
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.fetchProductionDepartments'))
            throw error
        }
    }

    async fetchJobNumbers(query) {
        try {
            return await qualityComplaintsQueries.fetchJobNumbers(query)
        } catch (error) {
            handleApiError(error, t('jobs.errors.fetchJobNumbers'))
            throw error
        }
    }

    async fetchOrderNumbers(query) {
        try {
            return await qualityComplaintsQueries.fetchOrderNumbers(query)
        } catch (error) {
            handleApiError(error, t('quality.complaints.errors.searchOrders'))
            throw error
        }
    }
}

export const useQualityComplaintsService = (options) => new QualityComplaintsService(options)
